import { useState } from "react";
import { format } from "date-fns";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

// Drop-in replacement for the native date input: same value/onChange(e.target.value) contract, ISO yyyy-mm-dd in and out.
const parseISO = (v) => {
  if (!v || !/^\d{4}-\d{2}-\d{2}/.test(v)) return undefined;
  const [y, m, d] = v.slice(0, 10).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? undefined : dt;
};

// Sticky header height (+ breathing room): every picker treats the bar as a wall, never opening under it.
const headerClearance = () => {
  const bar = typeof document !== "undefined" ? document.querySelector("header") : null;
  return Math.max(0, Math.round(bar?.getBoundingClientRect().bottom || 0)) + 8;
};

export function DateInput({ value, onChange, min, max, placeholder = "Pick a date", disabled = false, className = "", style, required, belowHeader = true, ...rest }) {
  const [open, setOpen] = useState(false);
  const [topPad, setTopPad] = useState(8);
  const handleOpen = (o) => { if (o && belowHeader) setTopPad(headerClearance()); setOpen(o); };
  const placement = belowHeader
    ? { side: "bottom", sideOffset: 6, collisionPadding: { top: topPad, left: 8, right: 8, bottom: 8 }, style: { maxHeight: "var(--radix-popover-content-available-height)" } }
    : {};
  const date = parseISO(value);
  const emit = (v) => onChange?.({ target: { value: v || "" } });
  const disabledDays = [];
  if (min && parseISO(min)) disabledDays.push({ before: parseISO(min) });
  if (max && parseISO(max)) disabledDays.push({ after: parseISO(max) });
  const testid = rest["data-testid"];

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          data-value={value || ""}
          aria-label={rest["aria-label"] || placeholder}
          className={`inline-flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm text-left outline-none transition-colors hover:bg-[#FAF8F3] disabled:opacity-60 ${className}`}
          style={{ borderColor: "var(--bk-border)", color: date ? "var(--bk-text)" : "var(--bk-text-2)", minWidth: 150, ...style }}
        >
          <CalendarBlank size={16} weight="duotone" color="var(--bk-accent)" className="shrink-0" />
          <span className="flex-1 truncate">{date ? format(date, "MMM d, yyyy") : placeholder}</span>
          {date && !required && !disabled && (
            <span role="button" aria-label="Clear date" data-testid={testid ? `${testid}-clear` : undefined} onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); emit(""); }} className="rounded-full p-0.5 hover:bg-black/5" style={{ color: "var(--bk-text-2)" }}><X size={12} weight="bold" /></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" {...placement} className={`w-auto p-0 rounded-2xl ${belowHeader ? "overflow-y-auto" : ""}`} data-testid={testid ? `${testid}-popover` : undefined}>
        <Calendar mode="single" selected={date} defaultMonth={date || new Date()} disabled={disabledDays.length ? disabledDays : undefined} initialFocus
          onSelect={(d) => { if (d) { emit(format(d, "yyyy-MM-dd")); setOpen(false); } }} />
        <div className="flex items-center justify-between px-3 pb-3 -mt-1">
          <button type="button" onClick={() => { emit(format(new Date(), "yyyy-MM-dd")); setOpen(false); }} data-testid={testid ? `${testid}-today` : undefined} className="text-xs font-semibold" style={{ color: "var(--bk-accent)" }}>Today</button>
          {!required && <button type="button" onClick={() => { emit(""); setOpen(false); }} className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Clear</button>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
